import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStateService } from '@/services/app-state.service';
import { LocationService, ToastService, UserService } from '@/services';
import { Account, AccountHierarchy, Product } from '@zagops/api/sdk-angular';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TreeTableModule } from 'primeng/treetable';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { Router } from '@angular/router';
import { AppAccountLogoComponent } from './app.accountlogo';

interface Column {
  field: string;
}

@Component({
  selector: 'app-location-selector',
  standalone: true,
  imports: [
    FormsModule,
    AppAccountLogoComponent,
    TreeSelectModule,
    ButtonModule,
    TreeTableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    CheckboxModule,
  ],
  providers: [LocationService],
  template: `
    @if (userService.accountNodeOptions) {
      <p-treetable
        #tt
        [value]="userService.accountNodeOptions"
        [scrollable]="true"
        filterMode="strict"
        [columns]="cols"
        class="p-treetable-sm table-auto"
        selectionMode="single"
        [(selection)]="userService.selectedAccountNode"
      >
        <ng-template #caption>
          <div class="flex justify-between items-end w-full mb-4">
            <p-iconfield class="items-end">
              <p-inputicon class="pi pi-search" />
              <input
                #accSelect
                type="text"
                class="font-normal"
                pInputText
                placeholder="Search for location"
                (input)="tt.filterGlobal(accSelect.value, 'contains')"
              />
            </p-iconfield>
          </div>
        </ng-template>

        <ng-template #body let-rowNode let-rowData="rowData">
          <tr [ttRow]="rowNode">
            @for (col of cols; track col; let i = $index) {
              <td>
                <div class="flex items-center">
                  @if (i === 0) {
                    <p-treetable-toggler [rowNode]="rowNode" />
                  }
                  <app-account-logo
                    [accountLogoScope]="rowNode.node.data"
                    variant="avatar"
                  />
                  <span class="ml-2">
                    {{ rowData[col.field] }}
                  </span>
                </div>
              </td>
            }

            <!-- Actions -->
            <td class="!text-right w-[110px]">
              @if (rowNode.node.selectable) {
                <p-button
                  [label]="isSelected(rowNode.node) ? 'Selected' : 'Select'"
                  size="small"
                  [severity]="
                    isSelected(rowNode.node) ? 'secondary' : 'primary'
                  "
                  [outlined]="isSelected(rowNode.node)"
                  (click)="handleChange(rowNode)"
                  [disabled]="isSelected(rowNode.node)"
                ></p-button>
              }
            </td>
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr>
            <td [attr.colspan]="cols.length">No Locations Found.</td>
          </tr>
        </ng-template>
      </p-treetable>
    }
  `,
})
export class AppAccountSelectorComponent implements OnInit {
  public options?: TreeNode[];
  public cols!: Column[];

  constructor(
    private appStateService: AppStateService,
    public userService: UserService,
    private toastService: ToastService,
    private router: Router,
  ) {}

  isSelected(node: TreeNode) {
    return node.data.id === this.appStateService.selectedAccountId;
  }

  async ngOnInit() {
    this.cols = [{ field: 'name' }];
  }

  getNodeIcon(accountType?: Account.type) {
    switch (accountType) {
      case Account.type.CONTRACTED:
        return 'pi pi-building';
      case Account.type.CUSTOMER_BRAND:
      case Account.type.BRAND_ACCOUNT:
        return 'pi pi-sitemap';
      default:
        return 'pi pi-shop';
    }
  }

  async handleChange(event: TreeNodeSelectEvent) {
    try {
      const { node } = event;
      const account: AccountHierarchy = node.data;

      const selectedOrgMember = this.userService.appUser.orgMembers?.find(
        (om) => om.accountId === account.id,
      );
      if (!selectedOrgMember) {
        this.toastService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'You do not have access to this account. Please contact ZagOps for support.',
        });
        return;
      }

      this.appStateService.handleChangeSelectedAccountScope({
        type: account.type,
        logoUrl: account.logoUrl,
        name: account.name,
        themeJson: account.themeJson,
        id: account.id,
        orgMember: selectedOrgMember,
        products: account.products as Product.key[],
      });
      this.close();

      const currentUrl = this.router.url;
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigateByUrl(currentUrl);
    } catch (err) {
      this.toastService.add({
        summary: 'Error selecting Account',
        detail: (err as Error).message,
        severity: 'error',
      });
    }
  }

  ref = inject(DynamicDialogRef);
  cfg = inject(DynamicDialogConfig);

  close() {
    this.ref.close();
  }
}
